import {
  Button,
  Card,
  Chip,
  Divider,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { RouterLink } from "components/router-link";
import viewpaths from "global/constants/ViewPathConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import history from "utils/history";
import { KeyboardBackspace } from "@mui/icons-material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  convertESTtoUserLocalDateAndTime,
  getABResultAfterType,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { getStatusColor } from "../Components/EmailDataList";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import { CampaignCount, CampaignOverviewType } from "models/interfaces";
import { campaignStatusEnum, campaignTypesEnum } from "../Constants";
import Overview from "./Components/Overview";
import ViewEmail from "./Components/ViewEmail";
import LinkActivity from "./Components/LinkActivity";
import ABTestDetails from "./Components/ABTestDetails";
import strings from "global/constants/StringConstants";
import { fetchChildCampaign } from "../CampaignService";
import SetManualDialog from "./Components/SetManualDialog";

interface CustomProps {
  campaignData: CampaignOverviewType;
  setCampaignData: Function;
  failedMetrics: CampaignCount[];
  refresh: Function;
}

const ViewABCampaign = (props: CustomProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const [tabs, setTabs] = useState<
    {
      label: string;
      value: string;
      key: string;
      disabled: boolean;
    }[]
  >([
    {
      label: "AB Test Summary",
      value: "ab-test-summary",
      key: "ab-test-summary",
      disabled: false,
    },
    {
      label: "Overview",
      value: "overview",
      key: "overview",
      disabled: true,
    },
    {
      label: "View Email",
      value: "view",
      key: "view",
      disabled: true,
    },
    {
      label: "Link Activity",
      value: "link",
      key: "link",
      disabled: true,
    },
  ]);
  const [currentTab, setCurrentTab] = useState(
    tabs[0]?.key ?? "ab-test-summary"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [childCampaignDetails, setChildCampaignDetails] = useState<
    CampaignOverviewType[]
  >([]);
  const [openManual, setManual] = useState(false);

  useEffect(() => {
    getChildVariantsData();
  }, []);

  useEffect(() => {
    if (props.campaignData.campaign.status === "Completed") {
      setTabs([...tabs.map((tab) => ({ ...tab, disabled: false }))]);
    }
  }, [props.campaignData]);

  const getChildVariantsData = async () => {
    try {
      setIsLoading(true);
      const response = await fetchChildCampaign(
        props.campaignData?.campaign?.id
      );
      setChildCampaignDetails(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const isAutomaticExecutionType = () => {
    return props.campaignData?.abTestInfo?.winingExecutionType === "Automatic";
  };

  const isCompleted = () => {
    return props.campaignData?.campaign?.status === strings.Completed;
  };

  const isSubmitted = () => {
    return props.campaignData?.campaign?.status === strings.Submitted;
  };

  const isChildCampaignExecuted = (): boolean => {
    return (
      isTruthy(childCampaignDetails) &&
      childCampaignDetails.every(
        (childCampaign: CampaignOverviewType) =>
          childCampaign.campaign?.status === strings.Completed
      )
    );
  };

  const generateMessage = () => {
    if (isCompleted()) {
      return `The campaign has been sent using the details and parameters from the winning campaign identified during the A/B testing phase.`;
    }
    if (isSubmitted()) {
      return `The campaign has been submitted using the details and parameters from the winning campaign identified during the A/B testing phase.`;
    } else {
      if (isAutomaticExecutionType()) {
        if (
          props.campaignData?.abTestInfo?.testingVariable === "scheduleTime"
        ) {
          return `The campaign that performs best in terms of ${
            props.campaignData?.abTestInfo?.winingMetric === "Opened"
              ? "open rate"
              : "click rate"
          } among  the scheduled times will be sent automatically sent at the winning time one day after the distribution of the variant campaigns. `;
        }
        return `The campaign that performs best in terms of ${
          props.campaignData?.abTestInfo?.winingMetric === "Opened"
            ? "open rate"
            : "click rate"
        } will be sent automatically ${getABResultAfterType(
          props.campaignData?.abTestInfo?.resultsAfterInterval!,
          props.campaignData?.abTestInfo?.resultsAfterType.toLowerCase()!
        )} after the distribution of the variant campaigns. `;
      }
      if (
        props.campaignData?.abTestInfo?.winingExecutionType === "Manual" &&
        !isChildCampaignExecuted()
      ) {
        return `The campaign that performs best in terms of ${
          props.campaignData?.abTestInfo?.winingMetric === "Opened"
            ? "open rate"
            : "click rate"
        } will be available for manual sending ${getABResultAfterType(
          props.campaignData?.abTestInfo?.resultsAfterInterval,
          props.campaignData?.abTestInfo?.resultsAfterType.toLowerCase()
        )} after the variant campaigns are sent. `;
      }
      if (
        props.campaignData?.abTestInfo?.winingExecutionType === "Manual" &&
        isChildCampaignExecuted()
      ) {
        return `The A/B test for this campaign has been completed. Review the statistics for both versions and choose which one to share with the remaining audience.`;
      }
    }
  };

  const getStatsHeader = () => {
    return (
      <>
        <Stack direction="row" alignItems="center">
          <Link
            color="text.primary"
            href={viewpaths.campaignsViewPath}
            sx={{
              alignItems: "center",
              display: "inline-flex",
            }}
            onClick={(e) => {
              e.preventDefault();
              history.goBack();
            }}
            underline="hover"
            component={RouterLink}
          >
            <KeyboardBackspace
              htmlColor="#475569"
              sx={{ cursor: "pointer", pr: 1 }}
            />
            <Typography fontSize="1rem" color="text.primary" fontWeight={700}>
              Campaigns
            </Typography>
          </Link>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: "6px",
            backgroundColor: "#7551D014",
          }}
          mt={2}
        >
          <Stack direction="row" alignItems="center" spacing={1} p={1}>
            <TipsAndUpdatesOutlinedIcon />
            <Typography variant="body2">{generateMessage()}</Typography>
          </Stack>
          {!isCompleted() &&
            isAutomaticExecutionType() &&
            props?.campaignData?.abTestInfo?.ratioForTesting !== 100 && (
              <Button
                variant="text"
                sx={{ height: "30px", color: theme.palette.primary.main }}
                onClick={() => setManual(true)}
              >
                Select Manually
              </Button>
            )}
        </Stack>
        <Stack
          direction={isDesktop ? "row" : "column"}
          justifyContent={"space-between"}
          alignItems={isDesktop ? "center" : "flex-start"}
          my={2}
          mb={1}
        >
          <Stack direction="column" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="h5"
                sx={{ color: theme.palette.primary.dark }}
              >
                {props.campaignData?.campaign?.name}
              </Typography>
              <Chip
                label={campaignStatusEnum[props.campaignData?.campaign?.status]}
                sx={{
                  color: getStatusColor(props.campaignData?.campaign?.status),
                  borderRadius: "5px",
                  minWidth: "90px",
                }}
                size="medium"
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography
                variant="body2"
                color={theme.palette.primary.dark}
                gutterBottom
              >
                {campaignTypesEnum[props.campaignData?.campaign?.type]} Campaign
              </Typography>
              <FiberManualRecordIcon
                fontSize="small"
                sx={{ width: "8px", color: theme.palette.primary.dark }}
              />{" "}
              <Typography
                variant="body2"
                color={theme.palette.primary.dark}
                gutterBottom
              >
                Created on{" "}
                {convertESTtoUserLocalDateAndTime(
                  props.campaignData?.campaign?.createdOn,
                  "MMMM D, YYYY [at] h:mm A"
                )}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        {props.campaignData?.campaign?.status === "Rejected" && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
            sx={{
              borderRadius: "6px",
              backgroundColor: "#fff2f2",
              maxWidth: "fit-content",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} p={1}>
              <Typography variant="body2" color="#c23118">
                The campaign has been rejected due to the detection of
                phishing-related content by our system
                <span>
                  <Link
                    underline="hover"
                    variant="body2"
                    onClick={() =>
                      window.open(
                        "https://mailzzy.com/help/campaigns/campaign-rejected/",
                        "_blank"
                      )
                    }
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {" "}
                    Learn more.
                  </Link>
                </span>
              </Typography>
            </Stack>
          </Stack>
        )}
      </>
    );
  };

  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      setCurrentTab(value);
    },
    []
  );

  const getActionTabs = () => {
    return (
      <Stack sx={{ mb: 2 }}>
        <Tabs
          indicatorColor="primary"
          onChange={handleTabsChange}
          scrollButtons="auto"
          textColor="primary"
          value={currentTab}
          variant="scrollable"
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              disabled={tab.disabled}
            />
          ))}
        </Tabs>
        <Divider flexItem sx={{ mb: 2, backgroundColor: "#47556940" }} />
      </Stack>
    );
  };

  const getTabsBody = () => {
    switch (currentTab) {
      case "ab-test-summary":
        return (
          <ABTestDetails
            campaignData={props.campaignData!}
            setCampaignData={props.setCampaignData}
            childCampaign={childCampaignDetails}
            setChildCampaign={setChildCampaignDetails}
          />
        );
      case "overview":
        return (
          <Overview
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            campaign={props.campaignData!}
            setCampaign={props.setCampaignData}
            failedMetrics={props.failedMetrics}
            showRetarget={false}
          />
        );
      case "view": {
        return <ViewEmail id={Math.abs(props.campaignData?.campaign?.id!)} />;
      }
      case "link": {
        return (
          <Card>
            <LinkActivity
              id={Math.abs(props.campaignData?.campaign?.id!)}
              showTotalClicks
            />
          </Card>
        );
      }
      default: {
        return <></>;
      }
    }
  };

  return (
    <>
      <CenteredAuthenticatedLayout>
        {getStatsHeader()}
        {getActionTabs()}
        {getTabsBody()}
      </CenteredAuthenticatedLayout>
      <SetManualDialog
        isDialogOpen={openManual}
        handleDialogClose={() => setManual(false)}
        campaignId={props.campaignData?.campaign?.id}
        refresh={props.refresh}
      />
    </>
  );
};

export default ViewABCampaign;
